import { unaryStreamRpc } from "@redotech/rpc/definition";
import * as z from "zod";

/**
 * Streaming events for the extension editor's "Agent Coder" agent. Field
 * names deliberately mirror the Ask Redo stream events so the client-side
 * transport translation into AI SDK `UIMessageChunk`s is the same proven
 * mapping (see ask-redo-rpc-transport.ts).
 */
const ExtensionArtifactAgentCoderStreamEventSchema_ = z.discriminatedUnion(
  "type",
  [
    z.object({ type: z.literal("text-delta"), textDelta: z.string() }),
    z.object({
      type: z.literal("tool-input-start"),
      toolCallId: z.string(),
      toolName: z.string(),
    }),
    z.object({
      type: z.literal("tool-input-delta"),
      toolCallId: z.string(),
      inputTextDelta: z.string(),
    }),
    z.object({
      type: z.literal("tool-call"),
      toolCallId: z.string(),
      toolName: z.string(),
      input: z.unknown().optional(),
    }),
    z.object({
      type: z.literal("tool-output-available"),
      toolCallId: z.string(),
      toolName: z.string(),
      output: z.unknown().optional(),
    }),
    z.object({
      type: z.literal("tool-output-error"),
      toolCallId: z.string(),
      toolName: z.string(),
      errorText: z.string(),
    }),
    z.object({ type: z.literal("reasoning"), text: z.string() }),
    z.object({
      type: z.literal("complete"),
      threadId: z.string(),
      /** The working code after the agent's edits; apply to the editor. */
      code: z.string(),
    }),
    z.object({ type: z.literal("error"), error: z.string() }),
  ],
);
export type ExtensionArtifactAgentCoderStreamEvent = z.infer<
  typeof ExtensionArtifactAgentCoderStreamEventSchema_
>;
export const ExtensionArtifactAgentCoderStreamEventSchema: z.ZodType<ExtensionArtifactAgentCoderStreamEvent> =
  ExtensionArtifactAgentCoderStreamEventSchema_;

/**
 * One turn of the extension editor's "Agent Coder" chat: the merchant's
 * message plus the editor's current code go in; the agent edits the code
 * through tools server-side while progress streams out. The terminal
 * `complete` event carries the threadId (pass it on subsequent turns to
 * continue the conversation) and the edited code.
 */
export const streamExtensionArtifactAgentCoder = unaryStreamRpc({
  name: "streamExtensionArtifactAgentCoder" as const,
  input: z.object({
    /** Omit to start a new conversation. */
    threadId: z.string().optional(),
    message: z.string(),
    contractId: z.string(),
    inputSelector: z.array(z.string()),
    /** The editor's current code — synced into the agent before the turn. */
    code: z.string(),
  }),
  output: ExtensionArtifactAgentCoderStreamEventSchema,
});
